import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBell, faTimes, faCaretDown, faCircle, faQuestion, faBars, faUserCircle } from '@fortawesome/free-solid-svg-icons'
import { MorphOrSpecies } from './utilities/MorphOrSpecies.tsx';
import { blankReptile, Reptile } from '../types/Reptile.ts';
import { UserInfo } from '../types/UserInfo.ts';
import { driver } from 'driver.js';
import { Placeholder, Offcanvas, Button } from 'react-bootstrap';
import { BasicFetch } from '../utils/BasicFetch.ts';
import { NewsModal } from './utilities/NewsModal.tsx';
import { RoadmapModal } from './utilities/RoadmapModal.tsx';
import { Notification } from '../types/Notifications.ts';
import { News } from '../types/News.ts';
import { ContactModal } from './utilities/ContactModal.tsx';
import { RatingModal } from './utilities/RatingModal.tsx';
import { SubscriptionModal } from './stripe/SubscriptionModal.tsx';
import { MissionStatementModal } from './utilities/MissionStatementModal.tsx';
import { ToolMenuButton } from './collection/Collection.tsx';
import { useRepToolContext } from '../RepToolContext.tsx';


const MenuControls = ({
  openProfile,
  userName,
  toggleTutorial,
  setShowNewsModal,
  setShowMenu,
}: {
  openProfile: () => void,
  toggleTutorial: () => void,
  userName: string,
  setShowNewsModal: () => void,
  setShowMenu: () => void
}) => {
  const [notifications, setNotifications] = useState([]);
  const [news, setNews] = useState([]);
  const checkMessages = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}news/get`,
      method: 'GET',
      onSuccess: ({ news }) => {
        setNews(news || []);
      }
    })
    BasicFetch({
      url: `${(window as any).serverLocation}notifications/get`,
      method: 'GET',
      onSuccess: ({ notifications }) => {
        setNotifications(notifications || []);
      }
    })
  }
  useEffect(() => {
    checkMessages();
  }, []);

  const unReadmessages: (Notification | News)[] = [...notifications, ...news].filter(message => !message.has_been_read);
  return (
    <>
      <div className='d-flex flex-row'>
        <button type="button" id="profile" onClick={openProfile} className="text-light ml-2 d-flex flex-row p-0 border-0 bg-transparent" >
          <FontAwesomeIcon icon={faUserCircle} size="1x" className="my-auto" />
          <h6 id="profile-message" className='ml-2 my-auto' >{userName}</h6>
        </button>
      </div>
      <div>
        <span className="mr-3" onClick={toggleTutorial} >
          <FontAwesomeIcon size='xl' icon={faQuestion} />
        </span>
        <span className="mr-3" onClick={setShowMenu} >
          <FontAwesomeIcon size='xl' icon={faBars} />
        </span>
        <span className="fa-layers fa-fw  my-auto" onClick={setShowNewsModal}>
          <FontAwesomeIcon size='xl' icon={faBell} className='my-auto' />
          {!!unReadmessages.length && <FontAwesomeIcon icon={faCircle} className='text-danger' transform="shrink-7 right-10 up-10" />}
        </span>
      </div>
    </>
  )
}

export const Menu = ({
  reptiles,
  reptileChanged,
  selectedReptile,
  openBrowse,
  openGeneCalc,
  openCareSheet,
  openCollection,
  openClutches,
  openProfile,
  openBountyList,
  openShop,
  loading
}: {
  reptiles: Reptile[],
  reptileChanged: (reptileAccronym: string) => void,
  selectedReptile: string | null,
  openBrowse: () => void,
  openGeneCalc: () => void,
  openCareSheet: () => void,
  openCollection: () => void,
  openClutches: () => void,
  openProfile: () => void,
  openBountyList: () => void,
  openShop: () => void,
  loading: boolean
}) => {
  const { appendControls, profileInfo, clearControls } = useRepToolContext();
  const [userName, setUserName] = useState(profileInfo?.first_name);
  const [showContact, setShowContact] = useState(false);
  const [showRoadMapModal, setShowRoadMapModal] = useState(false);
  const [currentReptiles, setCurrentReptiles] = useState(reptiles || []);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showMissionStatementModal, setShowMissionStatementModal] = useState(false);
  useEffect(() => {
    setUserName(profileInfo.first_name);
    updateTools();
    BasicFetch({
      url: `${(window as any).serverLocation}rating_request/check`,
      onSuccess: ({ rating }) => {
        if (rating) {
          setShowRatingModal(true);
        }
      }
    })
  }, [profileInfo])
  useEffect(() => {
    setCurrentReptiles(reptiles || [])
  }, [reptiles])

  useEffect(() => {
    updateTools();
  }, [userName])

  useEffect(() => {
    if (showContact || showNewsModal || showRatingModal || showSubscriptionModal || showMenu || showRoadMapModal || showMissionStatementModal) {
      appendControls(null, "Menu Modal Controls");
    } else {
      updateTools();
    }
  }, [showContact, showNewsModal, showRatingModal, showSubscriptionModal, showMenu, showRoadMapModal, showMissionStatementModal])

  const updateTools = () => {
    const driverObj = driver({
      steps: [
        { element: '#reptile-pick', popover: { description: 'Here you can quick change between all currently supported reptiles. Come back here when you want to select a different reptile.' } },
        { element: '#my-collection', popover: { title: 'My Collection', description: 'This tool allows you to record and manage your reptile collection.' } },
        { element: '#calculator-btn', popover: { title: 'Genetic Calculator', description: 'This is the genetic calculator. Here you can calculate the outcome of breeding two reptiles.' } },
        { element: '#browse-btn', popover: { title: 'Browse Morphs', description: 'Here you can browse through all morphs and genes for the selected reptile.' } },
        { element: '#caresheet-btn', popover: { title: 'Caresheet', description: 'Here you can view the care sheet for the selected reptile' } },
        { element: '#reptile-suggest', popover: { title: 'Suggest A Reptile', description: 'Here you can suggest a reptile to be added to the app.' } },
      ]
    });
    clearControls(<MenuControls openProfile={openProfile} toggleTutorial={() => driverObj.drive()} userName={userName || ''} setShowNewsModal={() => setShowNewsModal(true)} setShowMenu={() => setShowMenu(true)} />, "Menu")
  }
  const rep = reptiles.find(reptile => reptile.accronym === selectedReptile) || blankReptile;

  const controls = rep ? rep.controls : null;

  const openReptileSelector = () => {
    (window as any).setUpReptileSelect('', () => (reptile: string) => {
      reptileChanged(reptile);
    }, selectedReptile)
  }

  return (
    <>
      <Offcanvas className='bg-black border-right px-4 text-light' show={showMenu} onHide={() => setShowMenu(false)}>
        <Offcanvas.Header className='text-light d-flex justify-content-end' >
          <div onClick={() => { setShowMenu(false); }} className='close-canvas'>
            <FontAwesomeIcon icon={faTimes} size='2x' />
          </div>
        </Offcanvas.Header>


        <div className='d-flex flex-column mt-3'>
          <h5>Tools</h5>
        </div>
        <div>
          <ToolMenuButton userInfo={profileInfo} clickFunction={() => { openShop(); setShowMenu(false) }}>
            View My Shop
          </ToolMenuButton>
          <ToolMenuButton userInfo={profileInfo} clickFunction={() => { openBountyList(); setShowMenu(false); }}>
            Bounty List
          </ToolMenuButton>
          <ToolMenuButton userInfo={profileInfo} clickFunction={() => { setShowRoadMapModal(true); setShowMenu(false); }}>
            RepTool Roadmap
          </ToolMenuButton>
        </div>
        <div className='d-flex flex-column mt-3'>
          <h5>Info</h5>
        </div>

        <div>
          <ToolMenuButton userInfo={profileInfo} clickFunction={() => {
            setShowMissionStatementModal(true);
            setShowMenu(false);
          }}>
            RepTool's Mission
          </ToolMenuButton>
          <ToolMenuButton userInfo={profileInfo} clickFunction={() => { setShowContact(true); setShowMenu(false); }}>
            Contact Developer
          </ToolMenuButton>
        </div>
        <div className="mt-auto mb-3">

          Version: {(window as any).versionNumber}
        </div>

      </Offcanvas>
      <div className="app fadeIn animated">
        {showRatingModal && <RatingModal show={showRatingModal} onHide={() => setShowRatingModal(false)} />}
        {showNewsModal && <NewsModal closeModal={() => setShowNewsModal(false)} />}
        {showRoadMapModal && <RoadmapModal closeModal={() => setShowRoadMapModal(false)} />}
        {showSubscriptionModal && <SubscriptionModal displayFeatureRequiresMessage={true} userInfo={profileInfo} closeModal={() => setShowSubscriptionModal(false)} />}
        {showMissionStatementModal && <MissionStatementModal closeModal={() => setShowMissionStatementModal(false)} />}

        {showContact && <ContactModal closeModal={() => setShowContact(false)} onSend={() => setShowContact(false)} />}
        <div id="menu" className="d-flex flex-column h-100">
          <div id="menu-header" className="d-flex flex-column justify-content-between" style={{ marginTop: '2vh' }}>
            <div id="logo" className="text-center">
              <img src="images/logo.svg" />
            </div>

            <div className="text-center d-flex flex-column">
              <a className="home-button button mt-3 mx-auto d-flex" onClick={() => openReptileSelector()}>
                <div className='mx-auto'>
                  {rep.reptile_name || "Select A Reptile"}
                </div>
                <div>
                  <FontAwesomeIcon icon={faCaretDown} className="ml-2" />
                </div>
              </a>
              {
                loading && <div className='d-flex flex-column mx-auto' style={{ width: '54%' }}>
                  {Array.from(Array(6).keys()).map((i) => (
                    <div className='glow d-flex mt-3 overflow-hidden' style={{ height: 53, borderRadius: 21, }}>
                      <Placeholder.Button bg='dark' className='w-100 h-100 border-0' key={i} animation="wave" />
                    </div>
                  ))}
                </div>
              }

              {
                !loading && selectedReptile && selectedReptile !== "default" && currentReptiles.length > 0 && controls && <div id="tool-list" className='d-flex fade show flex-column'>
                  {controls.collection && <a id="my-collection" className="home-button button mt-3 mx-auto" onClick={() => openCollection()} >
                    My Collection
                  </a>}
                  {controls.geneticCalculator && <a id="calculator-btn" onClick={() => openGeneCalc()} className="home-button button mt-3 mx-auto">Genetic Calculator</a>}
                  {controls.browseMorphs && <a id="browse-btn" onClick={() => openBrowse()} className="home-button button mt-3 mx-auto">Browse <MorphOrSpecies reptile={rep} plural={true} /></a>}
                  {controls.caresheet && <a id="caresheet-btn" onClick={() => openCareSheet()} className="home-button button mt-3 mx-auto">Care Sheet</a>}
                  {!controls.caresheet && <a id="caresheet-btn" className="home-button disabled button mt-3 mx-auto" style={{ opacity: '.6' }}>Care Sheet (Coming Soon)</a>}
                  <a id="clutches-btn" onClick={openClutches} className="home-button button mt-3 mx-auto">
                    Clutches
                  </a>
                </div>
              }
              {controls && !controls.isActive && !loading && <span className='mt-3 text-light'>Reptile is inactive</span>}
            </div>
          </div>
        </div>
      </div>
    </>

  )
}